import React from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, MenuItem, Skeleton, TextField } from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ManualOrder from "../ManualOrder/ManualOrder";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { SalesOrder, getSalesOrders, markOrderComplete } from "../../common/requests/sales/endpoints";
import {
  Banner,
  DetailDialog,
  ErrorState,
  KpiCard,
  KpiStrip,
  LoadingRows,
  PageShell,
  Section,
  errorMessage,
  useResource,
  useToast,
  useUrlParam,
} from "../../ui";
import LifetimeStrip from "./components/LifetimeStrip";
import SalesFilters, { FilterOptions, Option } from "./components/SalesFilters";
import SalesTrend from "./components/SalesTrend";
import OrderPipeline from "./components/OrderPipeline";
import Breakdown from "./components/Breakdown";
import OrdersTable from "./components/OrdersTable";
import OrderDetailDialog from "./components/OrderDetailDialog";
import CompleteOrderDialog from "./components/CompleteOrderDialog";
import CancelOrderDialog from "./components/CancelOrderDialog";
import {
  DEFAULT_FILTERS,
  STATUS_ORDER,
  SaleLine,
  SalesFilterState,
  applyDimensionFilters,
  attentionReason,
  buildPipeline,
  change,
  downloadCsv,
  filtersFromParams,
  flattenOrders,
  formatCount,
  formatMoney,
  formatPercent,
  groupByOrder,
  inRange,
  linesToCsv,
  rankBy,
  resolveRange,
  statusRank,
  summarise,
  toDateInput,
} from "./salesData";
import { ORDER_SORTS, findOrder, mergeFilterParams } from "./orderView";

const grid = (columns: Record<string, string>) => ({ display: "grid", gap: 1.25, gridTemplateColumns: columns, minWidth: 0 });

function optionsFrom(lines: SaleLine[], key: (l: SaleLine) => string | null, label: (l: SaleLine) => string): Option[] {
  const map = new Map<string, string>();
  lines.forEach((l) => {
    const k = key(l);
    if (k !== null && !map.has(k)) map.set(k, label(l));
  });
  return Array.from(map, ([value, text]) => ({ value, label: text })).sort((a, b) => a.label.localeCompare(b.label));
}

export default function Sales() {
  const { idToken, adminUser } = useAuth();
  const canComplete = adminUser?.role === "superadmin";
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orderParam, setOrderParam] = useUrlParam("order");
  // Manual orders live here now; the old /manual-order route redirects to ?create=1.
  const [createOpen, setCreateOpen] = React.useState(() => searchParams.get("create") === "1");
  React.useEffect(() => {
    if (searchParams.has("create")) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("create");
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);
  const filters = React.useMemo(() => filtersFromParams(searchParams), [searchParams]);

  const updateFilters = React.useCallback(
    (patch: Partial<SalesFilterState>) =>
      setSearchParams((prev) => mergeFilterParams(prev, { ...filtersFromParams(prev), ...patch }), { replace: true }),
    [setSearchParams]
  );
  const clearFilters = () => updateFilters({ ...DEFAULT_FILTERS, range: filters.range, from: filters.from, to: filters.to });
  const toggleIn = (key: "statuses" | "sellers" | "buyers" | "currencies" | "countries" | "genera", value: string) =>
    updateFilters({
      [key]: filters[key].includes(value) ? filters[key].filter((v) => v !== value) : [...filters[key], value],
    } as Partial<SalesFilterState>);

  /* ── data ── */
  // Loads once a token exists; token refreshes don't refetch everything.
  const sales = useResource(
    async () => (await getSalesOrders(idToken as string)).data,
    [],
    { enabled: !!idToken, errorText: "Couldn't load sales." }
  );
  const data = sales.data;
  const [refreshing, setRefreshing] = React.useState(false);
  const [updatedAt, setUpdatedAt] = React.useState<Date | null>(null);
  React.useEffect(() => {
    if (data) setUpdatedAt(new Date());
  }, [data]);
  const load = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await sales.reload();
    } finally {
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sales.reload]);

  const [completing, setCompleting] = React.useState<SalesOrder | null>(null);
  const [completeBusy, setCompleteBusy] = React.useState(false);
  const [completeError, setCompleteError] = React.useState<string | null>(null);
  const [cancelling, setCancelling] = React.useState<SalesOrder | null>(null);
  const [sort, setSort] = React.useState(ORDER_SORTS[0].value);
  const sortDef = ORDER_SORTS.find((o) => o.value === sort) ?? ORDER_SORTS[0];

  const currency = data?.reporting_currency ?? "SGD";
  const showIdentity = data?.include_identity ?? false;

  /* ── derived ── */
  const allLines = React.useMemo(() => flattenOrders(data?.orders ?? []), [data]);
  const lifetime = React.useMemo(() => summarise(allLines), [allLines]);
  const dayKey = new Date().toDateString();
  const range = React.useMemo(
    () => resolveRange(filters.range, filters.from, filters.to),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.range, filters.from, filters.to, dayKey]
  );

  // "Needs attention" ignores the date range: a stuck order from two months
  // ago matters more than one from today.
  const scope = React.useCallback(
    (lines: SaleLine[]) =>
      filters.attention ? lines.filter((l) => attentionReason(l) !== null) : lines.filter((l) => inRange(l, range.start, range.end)),
    [filters.attention, range]
  );

  const filtered = React.useMemo(() => applyDimensionFilters(allLines, filters), [allLines, filters]);
  const current = React.useMemo(() => scope(filtered), [scope, filtered]);
  const currentSummary = React.useMemo(() => summarise(current), [current]);
  const previousSummary = React.useMemo(() => {
    if (!range.previous || filters.attention) return null;
    const { start, end } = range.previous;
    return summarise(filtered.filter((l) => inRange(l, start, end)));
  }, [filtered, range, filters.attention]);

  const pipeline = React.useMemo(
    () => buildPipeline(scope(applyDimensionFilters(allLines, filters, { skipStatus: true }))),
    [allLines, filters, scope]
  );
  const attentionOrders = React.useMemo(
    () => new Set(allLines.filter((l) => attentionReason(l) !== null).map((l) => l.order.order_id)).size,
    [allLines]
  );
  const groups = React.useMemo(() => groupByOrder(current), [current]);

  const breakdowns = React.useMemo(
    () => ({
      sellers: rankBy(current, (l) => l.seller_key, (l) => l.seller ?? "Unknown seller"),
      buyers: rankBy(current, (l) => l.order.buyer_key, (l) => l.order.buyer ?? "Unknown buyer"),
      genera: rankBy(current, (l) => l.genus ?? "Unknown", (l) => l.genus ?? "Unknown"),
      countries: rankBy(current, (l) => l.order.buyer_country ?? "Unknown", (l) => l.order.buyer_country ?? "Unknown"),
      currencies: rankBy(current, (l) => l.order.currency, (l) => `Paid in ${l.order.currency}`),
      buyerType: rankBy(
        current,
        (l) => (l.order.is_first_order ? "new" : "returning"),
        (l) => (l.order.is_first_order ? "First orders" : "Repeat orders")
      ),
      channel: rankBy(
        current,
        (l) => (l.is_special_offer ? "offer" : "listing"),
        (l) => (l.is_special_offer ? "Special offers" : "Listed price")
      ),
    }),
    [current]
  );

  const options: FilterOptions = React.useMemo(
    () => ({
      statuses: optionsFrom(allLines, (l) => l.status, (l) => l.status)
        .concat(
          (STATUS_ORDER as readonly string[])
            .filter((s) => !allLines.some((l) => l.status === s))
            .map((s) => ({ value: s, label: s }))
        )
        .sort((a, b) => statusRank(a.value) - statusRank(b.value)),
      sellers: optionsFrom(allLines, (l) => l.seller_key, (l) => l.seller ?? "Unknown seller"),
      buyers: optionsFrom(allLines, (l) => l.order.buyer_key, (l) => l.order.buyer ?? "Unknown buyer"),
      currencies: optionsFrom(allLines, (l) => l.order.currency, (l) => l.order.currency),
      countries: optionsFrom(allLines, (l) => l.order.buyer_country ?? "Unknown", (l) => l.order.buyer_country ?? "Unknown"),
      genera: optionsFrom(allLines, (l) => l.genus ?? "Unknown", (l) => l.genus ?? "Unknown"),
    }),
    [allLines]
  );

  const trendStart =
    (filters.attention ? currentSummary.firstSale : range.start) ??
    lifetime.firstSale ??
    new Date(range.end.getTime() - 30 * 86400000);

  const openOrder = React.useMemo(() => findOrder(data?.orders ?? [], orderParam), [data, orderParam]);

  /* ── actions ── */
  const handleExport = () => {
    const name = `inflos-sales_${filters.attention ? "needs-attention" : `${range.start ? toDateInput(range.start) : "all-time"}_to_${toDateInput(new Date(range.end.getTime() - 1))}`}.csv`;
    downloadCsv(name, linesToCsv(current, currency, showIdentity));
  };

  const handleConfirmComplete = async (order: SalesOrder) => {
    if (!idToken) return;
    setCompleteBusy(true);
    setCompleteError(null);
    try {
      const { data: result } = await markOrderComplete(order.order_id, idToken);
      const parts = [`${result.items_completed} item${result.items_completed === 1 ? "" : "s"} completed`];
      if (result.items_failed.length) parts.push(`${result.items_failed.length} failed, try again`);
      if (result.items_skipped.length) parts.push(`${result.items_skipped.length} refunded/unpaid left as is`);
      toast(parts.join(" · "), result.items_failed.length ? "error" : "success");
      setCompleting(null);
      await load();
    } catch (e: any) {
      setCompleteError(errorMessage(e, "Failed to mark order as complete"));
    } finally {
      setCompleteBusy(false);
    }
  };

  /* ── render ── */
  const deltaLabel = previousSummary ? "vs prev. period" : undefined;
  const d = (pick: (s: typeof currentSummary) => number | null) =>
    previousSummary ? change(pick(currentSummary), pick(previousSummary)) : null;
  const periodLabel = filters.attention ? "Needs attention · all time" : range.label;

  const actions = (
    <>
      {canComplete && (
        <Button variant="contained" startIcon={<AddShoppingCartIcon />} onClick={() => setCreateOpen(true)} sx={{ minHeight: 44 }}>
          Create order
        </Button>
      )}
      <Button
        variant="outlined"
        startIcon={<FileDownloadOutlinedIcon />}
        onClick={handleExport}
        disabled={!current.length}
        title={`Download the ${current.length} filtered order items as CSV`}
        sx={{ minHeight: 44 }}
      >
        Export CSV
      </Button>
      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={refreshing || !idToken} sx={{ minHeight: 44 }}>
        {refreshing ? "Refreshing…" : "Refresh"}
      </Button>
    </>
  );

  return (
    <PageShell
      title="Sales"
      subtitle={`All amounts in ${currency} at the exchange rate on the order date unless shown otherwise.${
        updatedAt ? ` Updated ${updatedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}.` : ""
      }`}
      actions={actions}
    >
      {sales.error && data && (
        <Banner
          tone="danger"
          title="Couldn't refresh."
          action={
            <Button size="small" variant="outlined" onClick={load}>
              Retry
            </Button>
          }
        >
          {sales.error}
        </Banner>
      )}

      {!data ? (
        sales.status === "error" ? (
          <Box sx={{ bgcolor: C.paper, border: `1px solid ${C.line}`, borderRadius: "8px" }}>
            <ErrorState message={sales.error ?? "Couldn't load sales."} onRetry={load} />
          </Box>
        ) : (
          <>
            <KpiStrip max={4}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={96} />
              ))}
            </KpiStrip>
            <Skeleton variant="rounded" height={88} />
            <Box sx={{ bgcolor: C.paper, border: `1px solid ${C.line}`, borderRadius: "8px" }}>
              <LoadingRows rows={6} />
            </Box>
          </>
        )
      ) : (
        <>
          <Section title="All time" subtitle="Ignores the filters below">
            <LifetimeStrip summary={lifetime} currency={currency} />
          </Section>

          {lifetime.fxMissingOrders > 0 && (
            <Banner tone="warning">
              {lifetime.fxMissingOrders} order{lifetime.fxMissingOrders > 1 ? "s use" : " uses"} a currency with no exchange
              rate on file and {lifetime.fxMissingOrders > 1 ? "are" : "is"} left out of {currency} totals.
            </Banner>
          )}

          <SalesFilters
            filters={filters}
            range={range}
            options={options}
            showIdentity={showIdentity}
            onChange={updateFilters}
            onClear={clearFilters}
            resultCount={current.length}
          />

          <Section title={periodLabel}>
            <KpiStrip max={6}>
              <KpiCard
                title="GMV"
                value={formatMoney(currentSummary.gmv, currency)}
                delta={d((s) => s.gmv)}
                deltaLabel={deltaLabel}
                sub={currentSummary.refundedValue > 0 ? `excl. ${formatMoney(currentSummary.refundedValue, currency)} refunded` : undefined}
                help="Items + delivery − discounts, converted to SGD. Refunded items are excluded; partially refunded ones count in full because refund amounts aren't recorded."
              />
              <KpiCard
                title="Platform revenue"
                value={formatMoney(currentSummary.platformRevenue, currency)}
                delta={d((s) => s.platformRevenue)}
                deltaLabel={deltaLabel}
                sub={`take rate ${formatPercent(currentSummary.takeRate)}`}
                help="What Inflos keeps: seller earnings total minus the seller's share, from seller_earnings."
              />
              <KpiCard
                title="Orders"
                value={formatCount(currentSummary.orders)}
                delta={d((s) => s.orders)}
                deltaLabel={deltaLabel}
                sub={`${formatCount(currentSummary.units)} units · ${formatCount(currentSummary.sellers)} sellers`}
              />
              <KpiCard
                title="Avg order value"
                value={formatMoney(currentSummary.aov, currency)}
                delta={d((s) => s.aov)}
                deltaLabel={deltaLabel}
                sub={currentSummary.discounts > 0 ? `${formatMoney(currentSummary.discounts, currency)} in discounts` : undefined}
              />
              <KpiCard
                title="Buyers"
                value={formatCount(currentSummary.buyers)}
                delta={d((s) => s.buyers)}
                deltaLabel={deltaLabel}
                sub={`${formatCount(currentSummary.newBuyers)} new · ${formatCount(currentSummary.returningBuyers)} returning`}
                help="New = this period contains their first ever paid order."
              />
              <KpiCard
                title="Needs attention"
                value={formatCount(attentionOrders)}
                sub={filters.attention ? "showing these orders" : "orders, all time · tap to view"}
                tone={attentionOrders > 0 ? "warning" : "default"}
                active={filters.attention}
                onClick={() => updateFilters({ attention: !filters.attention })}
                help="Unconfirmed after 3 days, not shipped 5 days after payment, in delivery for 14+ days, or in delivery without tracking."
              />
            </KpiStrip>
          </Section>

          <Box sx={grid({ xs: "minmax(0, 1fr)", lg: "minmax(0, 2fr) minmax(0, 1fr)" })}>
            <SalesTrend lines={current} start={trendStart} end={range.end} currency={currency} />
            <OrderPipeline stages={pipeline} selected={filters.statuses} onToggle={(s) => toggleIn("statuses", s)} currency={currency} />
          </Box>

          <Section title="Breakdowns" subtitle="By GMV in this period · tap a row to filter">
            <Box sx={grid({ xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" })}>
              {showIdentity && (
                <Breakdown title="Top sellers" rows={breakdowns.sellers} currency={currency} selected={filters.sellers} onToggle={(k) => toggleIn("sellers", k)} />
              )}
              {showIdentity && (
                <Breakdown title="Top buyers" rows={breakdowns.buyers} currency={currency} selected={filters.buyers} onToggle={(k) => toggleIn("buyers", k)} />
              )}
              <Breakdown title="By genus" rows={breakdowns.genera} currency={currency} selected={filters.genera} onToggle={(k) => toggleIn("genera", k)} />
              <Breakdown title="By destination" rows={breakdowns.countries} currency={currency} selected={filters.countries} onToggle={(k) => toggleIn("countries", k)} />
              <Breakdown title="By checkout currency" rows={breakdowns.currencies} currency={currency} selected={filters.currencies} onToggle={(k) => toggleIn("currencies", k)} />
              <Breakdown title="New vs repeat orders" rows={breakdowns.buyerType} currency={currency} selected={[]} />
              <Breakdown title="Listing vs special offer" rows={breakdowns.channel} currency={currency} selected={[]} />
            </Box>
          </Section>

          <Section
            title="Orders"
            subtitle={`${formatCount(groups.length)} orders · ${formatCount(current.length)} items`}
            actions={
              <TextField select size="small" label="Sort" value={sort} onChange={(e) => setSort(e.target.value)} sx={{ minWidth: 190 }}>
                {ORDER_SORTS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            }
          >
            <OrdersTable
              groups={groups}
              reportingCurrency={currency}
              showIdentity={showIdentity}
              sortKey={sortDef.key}
              sortDir={sortDef.dir}
              selectedId={orderParam?.trim() ?? null}
              onOpen={(g) => setOrderParam(g.order.order_id, true)}
            />
          </Section>
        </>
      )}

      <OrderDetailDialog
        open={!!orderParam}
        orderId={orderParam}
        loading={!data && sales.status !== "error"}
        found={openOrder}
        reportingCurrency={currency}
        showIdentity={showIdentity}
        canComplete={canComplete}
        onClose={() => setOrderParam(null)}
        onComplete={(o) => {
          setCompleteError(null);
          setCompleting(o);
        }}
        onCancel={setCancelling}
      />

      {canComplete && (
        <DetailDialog open={createOpen} onClose={() => setCreateOpen(false)} title="Create order without payment" size="md">
          <ManualOrder
            embedded
            onCreated={() => {
              setCreateOpen(false);
              load();
            }}
          />
        </DetailDialog>
      )}

      <CancelOrderDialog
        order={cancelling}
        onClose={() => setCancelling(null)}
        onRefunded={(result) => {
          setCancelling(null);
          const deducted = result.earnings.filter((e) => e.action === "deducted_from_next_payout").length;
          toast(
            `Order cancelled: ${result.items_refunded} item${result.items_refunded === 1 ? "" : "s"} refunded` +
              (deducted ? ` · ${deducted} seller payout${deducted === 1 ? "" : "s"} will be reduced` : "") +
              ". Remember to refund the buyer in Stripe."
          );
          load();
        }}
      />
      <CompleteOrderDialog
        order={completing}
        busy={completeBusy}
        error={completeError}
        onCancel={() => setCompleting(null)}
        onConfirm={handleConfirmComplete}
      />
    </PageShell>
  );
}
